import * as React from 'react';
import { createStyles, Button, Group, Badge } from '@mantine/core';
import { NavLink } from 'react-router-dom';
import { ColumnDef } from '@tanstack/react-table';
import { useTranslation } from 'react-i18next';
import { useQuery, useMutation } from '@tanstack/react-query';
import { notifications } from '@mantine/notifications';
import format from 'date-fns/format';

// API
import { CreateSurvey, queryClient } from '../../api';

// Components
import { StatusPill } from '../StatusPill';
import { TableStatic } from '../Table/TableStatic';
import { TablePaginated } from '../Table/TablePaginated';
import { fetchOrganizationSurveyData, fetchPaginatedSurveysData } from './helpers';

// Helpers
import { getCurrentLocale } from '../../helpers/locale';

// Hooks
import { useEnumDataSet } from '../Form/hooks/useEnumDataSet';
import { useDeviceIdentification } from '../../hooks/useDeviceIdentification';

// Contexts
import { ViewingAsContext } from '../../contexts/ViewingAs';

// Types
import { ISurveyFull, SurveyStatus, UserTypes, TableQueryParams } from '../../types';

const useStyles = createStyles((theme) => ({}));

interface SurveysTableProps {
    openLinkPrefix: string;
    clientId?: string;
    openInNewTab?: boolean;
    readOnly?: boolean;
    rightControl?: React.ReactNode;
}

const defaultSort = 'updatedAt';

export const SurveysTable: React.FC<SurveysTableProps> = ({
    clientId,
    openInNewTab,
    openLinkPrefix,
    readOnly,
    rightControl
}) => {
    const { classes } = useStyles();
    const { t: columnHeaderTrans } = useTranslation('components', { keyPrefix: 'tables.surveys.columns' });
    const { getTranslatedEnumValue } = useEnumDataSet();
    const { t } = useTranslation('common');
    const { userType } = React.useContext(ViewingAsContext);
    const columns = React.useMemo<ColumnDef<ISurveyFull>[]>(
        () => [
            {
                accessorKey: 'trigger',
                cell: (info) => (info.getValue() ? t(`surveyTriggers.${info.getValue()}`, { ns: 'common' }) : '--'),
                footer: (props) => props.column.id,
                header: () => columnHeaderTrans('type'),
                meta: {
                    filterDataSetKey: 'SURVEY_TRIGGERS',
                    searchable: true
                },
                enableColumnFilter: true,
                filterFn: clientId ? 'fuzzy' : 'multi'
            },
            {
                accessorFn: (row) => row.organization,
                id: 'organization',
                header: () => columnHeaderTrans('organization'),
                cell: (props) => {
                    if (!props.row.original.data?.ancillary?.organization) return null;
                    const { _id, name } = props.row.original.data?.ancillary?.organization;

                    return (
                        <Group style={{ flexWrap: 'nowrap' }}>
                            <NavLink to={`/admin/clients/clientId/${_id}`}>{name}</NavLink>
                        </Group>
                    );
                },
                enableColumnFilter: false
            },
            {
                accessorKey: 'relationshipManager',
                header: () => columnHeaderTrans('relationshipManager'),
                cell: (props) => {
                    const relManager = props.row.original?.data?.ancillary?.organization?.relationshipManager;
                    return getTranslatedEnumValue('CLIENT_RELATIONSHIP_MANAGER', relManager) || relManager;
                },
                footer: (props) => props.column.id,
                enableColumnFilter: true,
                meta: {
                    filterDataSetKey: 'CLIENT_RELATIONSHIP_MANAGER'
                },
                filterFn: clientId ? 'fuzzy' : 'multi'
            },
            {
                accessorKey: 'status',
                cell: (info) => {
                    const status = info.getValue() as SurveyStatus;

                    return <StatusPill status={status as SurveyStatus} />;
                },
                footer: (props) => props.column.id,
                header: () => columnHeaderTrans('status'),
                enableColumnFilter: true,
                meta: {
                    filterDataSetKey: 'SURVEY_STATUS',
                    searchable: true
                },
                filterFn: clientId ? 'fuzzy' : 'multi'
            },
            {
                accessorKey: 'dateSent',
                cell: (info) => (
                    <>
                        {info.getValue()
                            ? format(new Date(info.getValue() as string), 'PP', { locale: getCurrentLocale() })
                            : '--'}
                    </>
                ),
                footer: (props) => props.column.id,
                header: () => columnHeaderTrans('dateSent'),
                enableColumnFilter: false
            },
            {
                accessorKey: 'reminderLastSent',
                cell: (info) => (
                    <>
                        {info.getValue()
                            ? format(new Date(info.getValue() as string), 'PP', { locale: getCurrentLocale() })
                            : '--'}
                    </>
                ),
                footer: (props) => props.column.id,
                header: () => columnHeaderTrans('reminderSent'),
                enableColumnFilter: false
            },
            {
                id: 'actions',
                enableColumnFilter: false,
                cell: (props) => {
                    if (
                        readOnly ||
                        (userType !== UserTypes.ADMIN &&
                            props.row.original.status &&
                            props.row.original.status === SurveyStatus.UPCOMING)
                    )
                        return (
                            <Button style={{ opacity: 0, pointerEvents: 'none' }}>{t('open', { ns: 'common' })}</Button>
                        );
                    const isReviewable =
                        userType === UserTypes.ADMIN && props.row.original.status === SurveyStatus.SUBMITTED;
                    const isEditable = [SurveyStatus.PENDING, SurveyStatus.IN_PROGRESS, SurveyStatus.REJECTED].includes(
                        props.row.original.status
                    );

                    return (
                        <Group position="right" style={{ flexWrap: 'nowrap' }}>
                            <NavLink
                                to={`/${openLinkPrefix}/surveys/survey/${props.row.original.id}`}
                                {...(openInNewTab
                                    ? {
                                          rel: 'noopener noreferrer',
                                          target: '_blank'
                                      }
                                    : {})}
                            >
                                {userType === UserTypes.ADMIN ? (
                                    <>
                                        {isReviewable ? (
                                            <Button>{t('review', { ns: 'common' })}</Button>
                                        ) : (
                                            <Button variant="default">{t('view', { ns: 'common' })}</Button>
                                        )}
                                    </>
                                ) : (
                                    <>
                                        {isEditable ? (
                                            <Button>{t('open', { ns: 'common' })}</Button>
                                        ) : (
                                            <Button variant="default">{t('view', { ns: 'common' })}</Button>
                                        )}
                                    </>
                                )}
                            </NavLink>
                        </Group>
                    );
                }
            }
        ],
        []
    );

    return clientId ? (
        <ClientSurveysTable {...{ clientId, columns, rightControl }} />
    ) : (
        <AllSurveysTable {...{ columns, rightControl }} />
    );
};

interface ClientSurveysTableProps extends Omit<SurveysTableProps, 'openInNewTab' | 'openLinkPrefix'> {
    columns: ColumnDef<ISurveyFull, any>[];
}

export const ClientSurveysTable: React.FC<ClientSurveysTableProps> = ({ clientId, columns, rightControl }) => {
    const { data } = useQuery({
        queryKey: ['surveysByClientId', clientId],
        queryFn: async () => {
            const response = await fetchOrganizationSurveyData({ clientId });

            return response;
            // return userType === UserTypes.ADMIN
            //     ? response
            //     : {
            //           rows: (response?.rows ?? []).filter(({ status }) => status !== SurveyStatus.UPCOMING)
            //       };
        },
        keepPreviousData: false,
        refetchOnWindowFocus: false
    });
    const { userType } = React.useContext(ViewingAsContext);
    const { isDesktop, isDesktopWide, isDesktopExtraWide } = useDeviceIdentification();
    const createSurvey = useMutation(CreateSurvey, {
        onSuccess: (newData) => {
            queryClient.setQueryData<{ rows: ISurveyFull[] }>(['surveysByClientId', clientId], (oldData) => {
                if (oldData && oldData.rows) {
                    return {
                        ...oldData,
                        rows: [...oldData.rows, newData]
                    };
                } else {
                    return {
                        rows: [newData]
                    };
                }
            });
            notifications.show({
                title: 'Survey Created',
                message: 'A new survey has been created'
            });
        },
        onError: (err: any) => {
            if (!err?.error?.showMessage) {
                notifications.show({
                    title: 'Error creating survey',
                    message: 'Intake survey could not be created'
                });
            }
        }
    });

    return (
        <>
            <TableStatic<ISurveyFull>
                data={data?.rows}
                visibleColumns={{
                    organization: false,
                    updatedAt: isDesktop || isDesktopWide,
                    reminderLastSent: isDesktopExtraWide
                }}
                columns={columns}
                defaultSort={defaultSort}
                rightControl={rightControl}
                searchable
                noMargin
                enableColumnFilters
            />
        </>
    );
};

interface AllSurveysTableTable {
    columns: ColumnDef<ISurveyFull, any>[];
    rightControl?: React.ReactNode;
}

export const AllSurveysTable: React.FC<AllSurveysTableTable> = ({ columns, rightControl }) => {
    const { isDesktop, isDesktopWide, isDesktopExtraWide } = useDeviceIdentification();
    const { userType } = React.useContext(ViewingAsContext);

    return (
        <TablePaginated<ISurveyFull>
            queryKey={`allSurveys`}
            queryCb={async (options: TableQueryParams) => {
                const response = await fetchPaginatedSurveysData({ options });

                return response;
            }}
            columns={columns}
            defaultSort={defaultSort}
            rightControl={rightControl}
            searchable
            visibleColumns={{
                name: isDesktop || isDesktopWide,
                relationshipManager: isDesktopWide,
                updatedAt: isDesktop || isDesktopWide,
                reminderLastSent: isDesktopExtraWide
            }}
            noMargin
            enableColumnFilters
        />
    );
};
